import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { AlertError, AlertRateLimit } from "./Alert";
import { useTranslation } from "react-i18next";
import { SSE } from "./stream";
import tagManager from "./tags";
import posthog from "posthog-js";
import { ChatbotMessageChunk, AnyMessage } from "./types";
import { store, useAppDispatch, useAppSelector } from "../store";
import {
  useCurrentFrameSettings,
  usePrompt,
  useOnSignal,
  useCurrentFrameLocale,
  useLauncher,
  useIsFullscreen,
  useReduxStateChange,
} from "../hooks";
import {
  finishStreamingAndUpsertConversations,
  getPingRequestFromState,
} from "../actions/sharedActions";
import { setUserError, updateUserFromPing } from "../slices/userSlice";
import { Prompt } from "./Prompt";
import { MessageItem } from "./MessageItem";
import { useSyncSuggestions } from "../hooks";
import {
  clearChat,
  initialMessagesToChatMessages,
  prependInitialMessages,
  selectApiError,
  selectConversationId,
  selectIsLoading,
  selectMessages,
  selectOverrides,
  sendMessage,
  setApiError,
  setConversationId,
  setInitialMessages,
  setMessages,
  updateStreamingResponse,
} from "../slices/chatSlice";
import { useRouter } from "../router";
import { ArrowLeft, RefreshCw, ArrowDown } from "lucide-react";
import { useFramePubSub } from "../pubsub";
import useExclusive from "./useExclusive";
import deepEqual from "fast-deep-equal";
import { isIframe } from "../PositionMode";
import { setAttachments, setQuickReplies } from "../slices/promptSlice";
import { Toaster } from "sonner";
import { FileData } from "../types/api";
import ConversationRating from "./ConversationRating";

// TODO(liamvdv): abort handler for request (show "Abort request" button).
// TOOD(liamvdv): timeout request with correct UI updates.

export function ThinkingDot() {
  const { t } = useTranslation();
  const [dots, setDots] = useState("");
  const [showThinking, setShowThinking] = useState(false);

  useEffect(() => {
    // Animate Dots
    const interval = setInterval(() => {
      setDots((prevDots) => {
        if (prevDots === "...") return "";
        return prevDots + ".";
      });
    }, 250); // 1000/4 for quarter-second intervals

    // Switch to "thinking" after 1.5 seconds
    const timeout = setTimeout(() => {
      setShowThinking(true);
    }, 1500);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
    };
  }, []);

  const displayText = `${t(showThinking ? "thinking" : "understanding")} ${dots}`;

  return (
    <div className="ml-2 p-2 flex flex-row items-center gap-2">
      <div className="relative inline-flex">
        <div className="w-3 h-3 bg-brand rounded-full"></div>
        <div className="w-3 h-3 bg-brand rounded-full absolute top-0 left-0 animate-ping"></div>
        <div className="w-3 h-3 bg-brand rounded-full absolute top-0 left-0 animate-pulse"></div>
      </div>
      <div>
        <span className="text-sm text-slate-600">{displayText}</span>
      </div>
    </div>
  );
}

function useIsInline() {
  return useAppSelector((state) => state.config.data.mode) === "inline";
}

function Messages({
  messages,
  isLoading,
  messagesContainerRef,
  lastMessageRef,
}: {
  messages: AnyMessage[];
  isLoading: boolean;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  lastMessageRef: React.RefObject<HTMLDivElement>;
}) {
  const isInline = useIsInline() || isIframe();
  const scrollToBottom = () => {
    // if embed on page via iframe, this scrolls the parent window https://issues.chromium.org/issues/41375023.
    // would move the whole page on initial load.
    if (!isInline)
      lastMessageRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // scroll to bottom on new messages once.
  useEffect(() => {
    scrollToBottom();
  }, [messages.length]);

  return (
    <div>
      <div
        ref={messagesContainerRef}
        className="flex flex-col gap-3 max-w-3xl pt-2"
      >
        {messages.map((message, i) => (
          <MessageItem key={i} message={message} />
        ))}
        {isLoading && <ThinkingDot />}
        <ConversationRating />
        <div ref={lastMessageRef} />
      </div>
    </div>
  );
}

function setWebsiteContextTags() {
  const page = window.location.href;
  const domain = window.location.hostname;
  const ignore = ["chat.botbrains.io", "staging.chat.botbrains.io"];
  if (ignore.some((i) => domain.includes(i))) return;
  tagManager.setTags({
    "chat:website.url": page,
  });
}

function Layout({
  secondaryAction,
  children,
}: {
  secondaryAction: "restart" | "none" | "toConversations";
  children: React.ReactNode;
}) {
  // NOTE(liamvdv): I'm aware that if isHeaderless, users cannot refresh the chat [closing is not important in fullscreen mode]
  //                OMR Edu is the only main user here.
  const { data: settings } = useCurrentFrameSettings();
  const isGenerating = useAppSelector((state) => state.chat.isLoading);
  const { frameId, mode, experimentalListConversations } = useAppSelector(
    (state) => state.config.data
  );
  const isHeaderless = mode.includes("headerless");
  const { navigate } = useRouter();
  const dispatch = useAppDispatch();
  const { toggle: onClose, toggleExpand, isExpanded } = useLauncher();
  const refreshChat = () => dispatch(clearChat());

  const options = {
    restart: { icon: <RefreshCw className="w-5 h-5" />, action: refreshChat },
    toConversations: {
      icon: <ArrowLeft className="w-5 h-5" />,
      action: () => navigate(`/frames/${frameId}/conversations`),
    },
    none: { icon: undefined, action: undefined },
  };
  const { icon, action } = options[secondaryAction];
  const [hasError, setHasError] = useState<boolean>(false);

  const logo = (
    <div className="flex items-center">
      {typeof settings.header_image_url === "string" && !hasError ? (
        <img
          src={settings.header_image_url}
          alt="Logo"
          className="object-contain h-12 max-w-48"
          onError={() => setHasError(true)}
        />
      ) : null}
    </div>
  );

  const button = (
    label: string,
    icon: ReactNode | undefined,
    action: undefined | (() => void),
    pos: "left" | "right",
    disabled?: boolean
  ) => {
    return icon && action ? (
      <button
        onClick={action}
        className={`absolute ${pos == "left" ? "left-3" : "right-3"} top-2.5 p-3 text-header-close hover:bg-header-bg-hover rounded transition-colors disabled:cursor-not-allowed focus:outline-brand`}
        aria-label={label}
        disabled={disabled ?? false}
      >
        {icon}
      </button>
    ) : (
      <></>
    );
  };

  const closeButton = button(
    "Close Button",
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 4 4 20"></path>
      <path d="m4 4 16 16"></path>
    </svg>,
    onClose,
    "right"
  );
  const expandIcon = isExpanded ? (
    // Minimize icon
    <svg
      width="18"
      height="18"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      transform="matrix(-1,0,0,1,0,0)"
    >
      <path
        d="M13.59 5.31h-1.7l3.3-3.3-1.2-1.2-3.3 3.3v-1.7a.85.85 0 1 0-1.7 0v4.6h4.6a.85.85 0 1 0 0-1.7M1.57 9.84c0 .47.38.85.85.85h1.7l-3.3 3.3 1.2 1.2 3.3-3.3v1.7a.85.85 0 1 0 1.7 0v-4.6h-4.6a.85.85 0 0 0-.85.85"
        fill="currentColor"
      ></path>
    </svg>
  ) : (
    // Expand icon
    <svg
      width="18"
      height="18"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      transform="matrix(-1,0,0,1,0,0)"
    >
      <path
        d="M6.49001 8.30999L3.69 11.11V9.40999C3.69 8.93999 3.31 8.55999 2.84 8.55999C2.37 8.55999 1.99001 8.93999 1.99001 9.40999V14.01H6.59C7.06 14.01 7.44 13.63 7.44 13.16C7.44 12.69 7.06 12.31 6.59 12.31H4.89L7.69 9.50999L6.49001 8.30999ZM9.41 1.98999C8.94 1.98999 8.56001 2.36999 8.56001 2.83999C8.56001 3.30999 8.94 3.68999 9.41 3.68999H11.11L8.31001 6.48999L9.51 7.68999L12.31 4.88999V6.58999C12.31 7.05999 12.69 7.43999 13.16 7.43999C13.63 7.43999 14.01 7.05999 14.01 6.58999V1.98999H9.41Z"
        fill="currentColor"
      ></path>
    </svg>
  );

  let expandButton: ReactNode | undefined = button(
    isExpanded ? "Minimize Chat" : "Expand Chat",
    expandIcon,
    toggleExpand,
    experimentalListConversations ? "right" : "left"
  );

  if (mode == "inline" || mode == "fullscreen") {
    expandButton = undefined;
  }

  const isModeFullscreen = mode.includes("fullscreen");
  const inFullscreen = useIsFullscreen() || isModeFullscreen;
  let left, right;
  if (inFullscreen) {
    if (!isModeFullscreen) right = closeButton;
    if (secondaryAction === "restart") {
      left = button("Restart Chat", icon, action, "left", isGenerating);
    } else if (secondaryAction == "toConversations") {
      left = button(
        "Back to Conversation List",
        icon,
        action,
        "left",
        isGenerating
      );
    } else if (secondaryAction == "none") {
      left = undefined;
    } else {
      throw new Error("Invalid secondaryAction");
    }
  } else {
    // Base button positioning for secondary actions
    let baseLeft, baseRight;
    if (secondaryAction === "restart") {
      baseLeft = undefined;
      baseRight = button("Restart Chat", icon, action, "right", isGenerating);
    } else if (secondaryAction == "toConversations") {
      baseLeft = button(
        "Back to Conversation List",
        icon,
        action,
        "left",
        isGenerating
      );
      baseRight = undefined;
    } else if (secondaryAction == "none") {
      baseLeft = undefined;
      baseRight = undefined;
    } else {
      throw new Error("Invalid secondaryAction");
    }

    // Add expand button based on experimentalListConversations setting
    if (experimentalListConversations) {
      // Expand button on the right
      left = baseLeft;
      right = baseRight || expandButton;
    } else {
      // Expand button on the left
      left = baseLeft || expandButton;
      right = baseRight;
    }
  }

  return (
    <div className="w-full h-full relative font-sans bg-chat overscroll-hidden">
      <div className="relative h-full w-full transition-width flex flex-col overflow-hidden items-stretch flex-1">
        <div className="flex flex-col h-full">
          {!isHeaderless && (
            <div
              className="relative flex shrink-0 items-center justify-center h-16 bg-header-bg px-[20px] py-2"
              aria-label="Chat header"
            >
              {/* Action Button */}
              {left && left}
              {/* Centered Logo */}
              {logo}
              {/* Close Button */}
              {right && right}
            </div>
          )}
          <Toaster position="top-center" visibleToasts={11} richColors />
          {children}
        </div>
      </div>
    </div>
  );
}
export { Layout };

function isNetworkError(error: unknown): boolean {
  // Check if it's a TypeError (fetch errors)
  if (error instanceof TypeError) {
    return true;
  }

  // Check for offline/network disconnected
  if (
    error instanceof DOMException &&
    ["NetworkError", "AbortError", "InternetDisconnectedError"].includes(
      error.name
    )
  ) {
    return true;
  }

  // For cross-browser support, check if error has a name property
  if (error && typeof error === "object" && "name" in error) {
    const errorName = (error as { name: string }).name;
    return errorName.includes("Network") || errorName.includes("Connection");
  }

  return false;
}

function ChatError({
  apiError,
  frameSettings,
}: {
  apiError: string;
  frameSettings: any;
}) {
  try {
    const errorData = JSON.parse(apiError);
    if (errorData.status === 429 && errorData.body?.detail?.retry_after) {
      return <AlertRateLimit retryAfter={errorData.body.detail.retry_after} />;
    }
  } catch {
    // Not JSON, fallback to regular error
  }

  return (
    <AlertError
      title={`I failed to finish my thoughts. Try again, please? Pardon me, ${frameSettings.data.assistant_name}`}
    >
      {apiError}
    </AlertError>
  );
}

const OtherTabGeneratingBlocker = () => {
  const { t } = useTranslation();
  return (
    <div className="py-2 px-[20px]">
      <div>{t("noConversationBranching")}</div>
    </div>
  );
};

// NOTE(liamvdv): fix: this is a hack to check if the first message is a user message.
//                The next generation of the Message History API will also store the initial message.
//                We need to change our behaviour in some spots to still work with v1.
const isV1MessageHistory = (messages: AnyMessage[]) =>
  messages.findIndex((m) => m.role === "user") === 0;

export default function Chat() {
  const dispatch = useAppDispatch();
  const frameSettings = useCurrentFrameSettings();
  const { locale, status: localeStatus } = useCurrentFrameLocale();
  const { publish } = useFramePubSub();
  useSyncSuggestions();

  const { data: config } = useAppSelector((state) => state.config);
  const { data: conversations, status: conversationsStatus } = useAppSelector(
    (state) => state.conversations
  );
  const suggestions = useAppSelector((state) => state.prompt.suggestions) || [];
  const messages = useAppSelector(selectMessages);
  const conversationId = useAppSelector(selectConversationId);
  const attachments = useAppSelector((state) => state.prompt.files);
  const isLoading = useAppSelector(selectIsLoading);
  const apiError = useAppSelector(selectApiError);
  const overrides = useAppSelector(selectOverrides);
  // State to track if we're currently rate limited
  const [isRateLimited, setIsRateLimited] = useState(false);

  // Check if current error is an active rate limit and update state
  useEffect(() => {
    if (!apiError) {
      setIsRateLimited(false);
      return;
    }

    try {
      const errorData = JSON.parse(apiError);
      if (errorData.status === 429 && errorData.body?.detail?.retry_after) {
        const retryTime = new Date(errorData.body.detail.retry_after).getTime();

        const updateRateLimit = () => {
          const now = new Date().getTime();
          const stillLimited = retryTime > now;
          setIsRateLimited(stillLimited);

          if (!stillLimited) {
            // Clear the error when rate limit expires
            dispatch(setApiError(""));
          }
        };

        updateRateLimit();
        const interval = setInterval(updateRateLimit, 1000);

        return () => clearInterval(interval);
      }
    } catch {
      // Not JSON or parsing failed
    }

    setIsRateLimited(false);
  }, [apiError, dispatch]);
  const { isLocked, withLock } = useExclusive(config.frameId, conversationId);
  const {
    text: prompt,
    setText: setPrompt,
    isDisabled,
    isShow: isShowPrompt,
  } = usePrompt();

  // Scroll to bottom functionality
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const isInline = useIsInline() || isIframe();

  const scrollToBottom = useCallback(() => {
    // if embed on page via iframe, this scrolls the parent window https://issues.chromium.org/issues/41375023.
    // would move the whole page on initial load.
    if (!isInline)
      lastMessageRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [isInline]);

  const checkScrollPosition = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      100;
    setShowScrollToBottom(!isNearBottom);
  }, []);

  // Check scroll position when scrolling
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener("scroll", checkScrollPosition);
    return () => container.removeEventListener("scroll", checkScrollPosition);
  }, [checkScrollPosition]);

  // *********************** Handle Locale Changes ************************** //
  // If no conversation started yet AND the messages are just the initial messages, update the intro message
  // if the locale changes.
  const allInitial =
    messages.every((m) => !!m.isInitial) && messages.length > 0;
  useReduxStateChange(
    (state) => state.config.data.locale,
    () => {
      if (allInitial) dispatch(setInitialMessages(locale?.messages ?? []));
    }
  );

  // *********************** Set Initial Messages *************************** //
  // NOTE(liamvdv): backwards compatibility for missing initial messages.
  //
  // Set message to initial messages if no messages are present yet.
  const isMissingIntroMessage =
    messages.length === 0 || isV1MessageHistory(messages);
  useEffect(() => {
    if (localeStatus === "succeeded" && isMissingIntroMessage)
      dispatch(prependInitialMessages(locale?.messages ?? []));
  }, [localeStatus, locale?.messages, isMissingIntroMessage]);

  // ******************* Sync Messages from API ***************************** //
  // NOTE(liamvdv): We pull in background updates only when not loading (still a race condition with message rating possible.)
  const apiConversation = conversations.find(
    (c) => c.conversation_id === conversationId
  );
  const localCoreMessages = messages.filter((m) => !m.isInitial);
  const remoteCoreMessages =
    apiConversation?.messages.filter((m) => !m.isInitial) ?? [];
  const isUpdateAvailable =
    // remote can also have updated metadata; thus larger equal.
    localCoreMessages.length <= remoteCoreMessages.length &&
    !deepEqual(remoteCoreMessages, localCoreMessages);
  useEffect(() => {
    // do not sync conversation if currently generating.
    if (isLoading) return;
    if (!apiConversation) return;
    if (isUpdateAvailable) {
      // NOTE(liamvdv): we want to keep newly suggested messages at the end of the input.
      const tailIntro = messages.slice(
        messages.findLastIndex((m) => !m.isInitial) + 1
      );
      // NOTE(liamvdv): backwards compatibility for missing initial messages in v1 history api.
      const apiMessages = isV1MessageHistory(apiConversation.messages)
        ? [
            ...initialMessagesToChatMessages(locale?.messages ?? []),
            ...apiConversation.messages,
          ]
        : apiConversation.messages;
      const mergedMessages = [...apiMessages, ...tailIntro];
      dispatch(setMessages(mergedMessages));
    }
  }, [conversationsStatus, apiConversation, isUpdateAvailable, isLoading]);

  const handleSend = useCallback(
    (value: string, files?: FileData[]) => {
      if (isDisabled) {
        window.alert("Send is not possible in this view.");
        return;
      }
      if (isRateLimited) {
        // Rate limit is active, prevent sending but don't show alert (the rate limit message is already visible)
        return;
      }
      setPrompt("");
      dispatch(setQuickReplies([]));
      const effectiveAttachments = files ?? attachments;
      dispatch(setAttachments([]));
      if (!posthog.sessionRecordingStarted()) posthog.startSessionRecording();

      const pingRequestData = getPingRequestFromState(store.getState());
      setWebsiteContextTags();

      performance.mark("chat_stream_start");

      const generator = dispatch(
        sendMessage({
          value,
          attachments: effectiveAttachments,
          conversationId,
          config,
          extraHeaders: [],
          pingRequestData,
          previousMessages: messages,
          tags: tagManager.getTags(),
          overrides,
        })
      ).then(({ payload: { url, headers, payload } }: any) => {
        let text = "";
        let analyticsProps = {
          isSuggestion: suggestions.includes(value),
          frameId: config.frameId,
          error: undefined as string | undefined,
          messageId: undefined as any,
        };

        const onData = ({
          data,
          headers,
        }: {
          data: ChatbotMessageChunk;
          headers: Headers;
          cancel: (reason: any) => void;
        }) => {
          // NOTE(liamvdv): hack to share the current message_id with the onEnd function.
          analyticsProps.messageId = data.metadata.message_id;

          dispatch(setConversationId(data.metadata.conversation_id));

          dispatch(
            updateUserFromPing({
              frameId: config.frameId,
              headers: {
                session_id: headers.get("X-Session-Id") ?? undefined,
                user_id: headers.get("X-User-Id") ?? undefined,
                device_id: pingRequestData.device_id,
              },
              user_data: data.user_data,
            })
          );

          if (data.text !== undefined) {
            text += data.text;
          }

          dispatch(
            updateStreamingResponse({
              text,
              metadata: data.metadata,
              messageId: data.metadata.message_id,
            })
          );
        };

        const onError = ({
          error,
          signal,
        }: {
          error: unknown;
          signal: AbortSignal;
        }) => {
          console.error(error);
          let message =
            "Sorry, we encountered an error. Please reload the browser page.";
          let errorData;

          // Check for timeout/abort errors
          if (signal.aborted) {
            message = signal.reason.message;
          } else if (isNetworkError(error)) {
            message =
              "Internet connection lost. Please check your connection. Reload the browser page to try again.";
          } else if (error instanceof Error) {
            message = error.message;
            try {
              errorData = JSON.parse(error.message);
              // For 429 errors, preserve the full error structure for rate limit display
              if (errorData.status === 429) {
                message = JSON.stringify(errorData);
              }
            } catch (e) {
              console.error("Error parsing error message:", e);
            }
          } else if (typeof error === "string") {
            message = error;
            try {
              // Try to parse string error as JSON too
              const parsedError = JSON.parse(error);
              if (parsedError.status === 429) {
                errorData = parsedError;
              }
            } catch (e) {
              // Not JSON, keep as string
            }
          }

          // NOTE(memben): setting duplicate errors to avoid refactor
          //               feel free to unify these error states in the future
          dispatch(setApiError(message));
          dispatch(setUserError(errorData));

          analyticsProps.error = message;
        };

        const onEnd = (status: any) => {
          performance.mark("chat_stream_end");
          const measure = performance.measure(
            "chat_message_duration",
            "chat_stream_start",
            "chat_stream_end"
          );

          const props = {
            ...analyticsProps,
            stream: true,
            status,
            startTime: measure.startTime,
            duration: measure.duration,
            textLength: text.length,
            conversationId: conversationId,
          };

          posthog.capture("api_chat_message", props);
          if (status === "succeeded") {
            dispatch(
              finishStreamingAndUpsertConversations({
                messageId: analyticsProps.messageId,
              })
            );
          } else {
            console.error("Error in chat stream.");
            setPrompt(value);
            setAttachments(effectiveAttachments);
            const isPreStreamError = analyticsProps.messageId === undefined;
            if (isPreStreamError) {
              // reset to original conversation (overwrite human message added)
              dispatch(setMessages(messages));
            } else {
              // reset to before the assistant message started.
              dispatch(setMessages(messages.slice(0, messages.length - 1)));
            }
            dispatch(setConversationId(undefined));
          }
          publish({
            type: "message_end",
            message_id: analyticsProps.messageId,
            conversation_id: conversationId!,
          });
        };

        publish({
          type: "message_start",
          conversation_id: conversationId,
        });
        return SSE(url, {
          headers: new Headers(headers),
          payload,
          onData,
          onError,
          onEnd,
        });
      });
      withLock(generator);
    },
    [
      messages,
      conversationId,
      suggestions,
      dispatch,
      attachments,
      overrides,
      isDisabled,
      isRateLimited,
    ]
  );

  useOnSignal("chat.send", () => handleSend(prompt));

  // by default, target is "restart"
  const target = useAppSelector(
    (state) => state.config.data.experimentalListConversations
  )
    ? "toConversations"
    : "restart";

  const quickReplies =
    useAppSelector((state) => state.prompt.quickReplies) || [];

  // We do not want to show quick replies if
  // - we are generating
  // - we do NOT have an existing conversation (use suggestions)
  // - handoff or another mechanism is active such that the prompt is hidden.
  const showQuickReplies =
    messages.length !== 1 && quickReplies.length > 0 && !isLoading; // TODO(liamvdv): add API call to generate quick replies / use messages[messages.length-1].metadata.quick_replies
  // We do not want to show suggestions if
  // - we are generating
  // - we have an existing conversation
  const showSuggestions = messages.length === 1 && suggestions.length > 0;

  return (
    <Layout secondaryAction={target}>
      <div
        className="flex-grow overflow-hidden px-[20px] relative"
        aria-label="Chat conversation"
      >
        <div
          ref={messagesContainerRef}
          className="h-full max-w-3xl mx-auto overscroll-y-contain overflow-y-auto no-scrollbar scrollable"
          onScroll={checkScrollPosition}
        >
          <Messages
            messages={messages}
            isLoading={isLoading}
            messagesContainerRef={messagesContainerRef}
            lastMessageRef={lastMessageRef}
          />
        </div>

        {showScrollToBottom && !isInline && (
          <button
            onClick={scrollToBottom}
            className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-brand hover:bg-brand-hover text-chat p-2 rounded-full shadow-lg transition-all duration-200 focus:outline-brand z-10"
            aria-label="Scroll to bottom"
          >
            <ArrowDown className="w-4 h-4" />
          </button>
        )}
      </div>
      {apiError && (
        <div className="py-2 px-[20px]">
          <ChatError apiError={apiError} frameSettings={frameSettings} />
        </div>
      )}
      {isLocked && <OtherTabGeneratingBlocker />}

      {isShowPrompt && (
        <section
          className="flex-shrink-0 w-full max-w-3xl mx-auto px-[20px]"
          role="region"
          aria-label="Chat input area"
        >
          <SuggestionsOrQuickReplies
            suggestions={suggestions}
            showSuggestions={showSuggestions}
            quickReplies={quickReplies}
            showQuickReplies={showQuickReplies}
            isDisabled={isDisabled}
            onSend={handleSend}
          />

          <div className="w-full">
            <Prompt
              value={prompt}
              isLoading={isLoading || isLocked || isRateLimited}
              isDisabled={isDisabled}
              setValue={setPrompt}
              onSubmit={handleSend}
            />
          </div>
        </section>
      )}
    </Layout>
  );
}

function SuggestionsOrQuickReplies({
  suggestions,
  showSuggestions,
  quickReplies,
  showQuickReplies,
  isDisabled,
  onSend,
}: {
  suggestions: string[];
  showSuggestions: boolean;
  quickReplies: string[];
  showQuickReplies: boolean;
  isDisabled: boolean;
  onSend: (value: string) => void;
}) {
  const { t } = useTranslation();
  if (showSuggestions)
    return (
      <div className="mb-2.5">
        <p className="text-sm text-chat-text-light text-center mb-2">
          {t("suggestions")}
        </p>
        {/* NOTE(liamvdv): we want to fill up bottom to top. but keep the visual order. Thus the doubel rotates, flex-wrap-reverse would break human understood order behaviour. */}
        <div
          className="flex flex-wrap gap-2 justify-start"
          style={{ transform: "rotate(180deg)" }}
        >
          {[...suggestions].reverse().map((suggestion) => (
            <button
              disabled={isDisabled}
              key={suggestion}
              onClick={() => onSend(suggestion)}
              className="border-chat text-chat bg-brand hover:bg-brand-hover px-4 py-1.5 rounded-[24px] text-left disabled:cursor-not-allowed"
              style={{ transform: "rotate(180deg)" }}
            >
              <p className="text-sm">{suggestion}</p>
            </button>
          ))}
        </div>
      </div>
    );
  if (showQuickReplies)
    return (
      <div className="mb-2.5">
        <div className="flex flex-wrap gap-2 justify-end">
          {quickReplies.map((qr) => (
            <button
              disabled={isDisabled}
              key={qr}
              onClick={() => onSend(qr)}
              className="border-chat text-chat bg-brand hover:bg-brand-hover px-4 py-1.5 rounded-[24px] text-left disabled:cursor-not-allowed"
            >
              <p className="text-sm">{qr}</p>
            </button>
          ))}
        </div>
      </div>
    );
  return null;
}
